import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaEnvelope,
  FaUser,
  FaSyncAlt,
  FaExclamationTriangle,
  FaTicketAlt,
  FaClock,
  FaTag,
  FaPaperPlane,
  FaTimes,
  FaMagic,
  FaPaperclip,
  FaTrash,
  FaStore,
  FaCrown,
  FaPhoneAlt,
  FaBrain,
  FaComments,
  FaMapMarkerAlt,
  FaPen,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { outcomeChips, formatCallDay, formatDuration as formatVoiceDuration } from '../utils/voiceFormat';
import './VoiceTickets.css';
import './ClientDetails.css';

const CLIENT_FETCH_BATCH_SIZE = 200;
const TICKETS_PAGE_SIZE = 25;

const formatDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString();
};

const formatAddress = (addr) => {
  if (!addr) return '';
  if (typeof addr === 'string') return addr;
  if (addr.formatted) return addr.formatted;
  const locality = [addr.postal_code, addr.city].filter(Boolean).join(' ');
  return [addr.line1, locality, addr.country].filter(Boolean).join(', ');
};

const ClientDetails = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [client, setClient] = useState(location.state?.client || null);
  const [clientLoading, setClientLoading] = useState(!client);
  const [clientError, setClientError] = useState(null);

  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [ticketsError, setTicketsError] = useState(null);
  const [ticketsPage, setTicketsPage] = useState(1);
  const [ticketsHasMore, setTicketsHasMore] = useState(false);
  const [ticketsRefreshing, setTicketsRefreshing] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: '', content: '', senderEmail: '' });
  const [emailFiles, setEmailFiles] = useState([]);
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [emailSuccess, setEmailSuccess] = useState(null);
  const [aiAssistantLoading, setAiAssistantLoading] = useState(false);
  const [vipUpdating, setVipUpdating] = useState(false);
  const [voiceData, setVoiceData] = useState(null);
  const [voiceLoading, setVoiceLoading] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone_number: '', address_line: '', postal_code: '', city: '', country: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const fileInputRef = useRef(null);

  const authHeaders = useMemo(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
    if (!token) return null;
    return {
      Authorization: `Bearer ${token}`,
    };
  }, []);

  useEffect(() => {
    if (!authHeaders) {
      navigate('/');
    }
  }, [authHeaders, navigate]);

  const fetchClientDetails = useCallback(async () => {
    if (client) {
      setClientLoading(false);
      return;
    }

    if (!authHeaders) {
      return;
    }

    setClientLoading(true);
    setClientError(null);

    let skip = 0;

    try {
      while (true) {
        const response = await axios.get('/api/clients', {
          headers: authHeaders,
          params: {
            skip,
            limit: CLIENT_FETCH_BATCH_SIZE,
          },
        });

        const payload = Array.isArray(response.data) ? response.data : [];
        const found = payload.find((entry) => entry.id === clientId);
        if (found) {
          setClient(found);
          break;
        }

        if (payload.length < CLIENT_FETCH_BATCH_SIZE) {
          setClientError('Client not found');
          break;
        }

        skip += CLIENT_FETCH_BATCH_SIZE;
      }
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Failed to load client details';
      setClientError(message);
    } finally {
      setClientLoading(false);
    }
  }, [authHeaders, client, clientId]);

  const fetchTickets = useCallback(
    async (pageArg = 1) => {
      if (!authHeaders) {
        return;
      }

      setTicketsLoading(pageArg === 1);
      setTicketsRefreshing(pageArg !== 1);
      setTicketsError(null);

      try {
        const response = await axios.get(`/api/clients/${clientId}/tickets`, {
          headers: authHeaders,
          params: {
            skip: (pageArg - 1) * TICKETS_PAGE_SIZE,
            limit: TICKETS_PAGE_SIZE,
          },
        });

        const payload = Array.isArray(response.data) ? response.data : [];
        setTickets(payload);
        setTicketsHasMore(payload.length === TICKETS_PAGE_SIZE);
        setTicketsPage(pageArg);
      } catch (err) {
        const message = err.response?.data?.detail || err.message || 'Failed to load tickets';
        setTicketsError(message);
        setTickets([]);
        setTicketsHasMore(false);
      } finally {
        setTicketsLoading(false);
        setTicketsRefreshing(false);
      }
    },
    [authHeaders, clientId],
  );

  const fetchVoice = useCallback(async () => {
    if (!authHeaders) {
      return;
    }
    setVoiceLoading(true);
    try {
      const response = await axios.get(`/api/clients/${clientId}/voice`, { headers: authHeaders });
      setVoiceData(response.data || null);
    } catch (err) {
      // Voice data is optional; ignore failures (e.g. email-only clients).
      setVoiceData(null);
    } finally {
      setVoiceLoading(false);
    }
  }, [authHeaders, clientId]);

  useEffect(() => {
    fetchClientDetails();
  }, [fetchClientDetails]);

  useEffect(() => {
    fetchTickets(1);
  }, [fetchTickets]);

  useEffect(() => {
    fetchVoice();
  }, [fetchVoice]);

  const handleBack = () => {
    navigate('/clients');
  };

  const handleTicketClick = (ticketId) => {
    navigate(`/tickets/${ticketId}/messages`);
  };

  const handleRetryTickets = () => {
    fetchTickets(ticketsPage);
  };

  const handleNextPage = () => {
    if (ticketsHasMore && !ticketsLoading) {
      fetchTickets(ticketsPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (ticketsPage > 1 && !ticketsLoading) {
      fetchTickets(ticketsPage - 1);
    }
  };

  const handleRefresh = () => {
    fetchTickets(ticketsPage);
  };

  const openEmailModal = () => {
    setEmailError(null);
    setEmailSuccess(null);
    setIsEmailModalOpen(true);
  };

  const closeEmailModal = (force = false) => {
    if (emailSending && !force) return;
    setIsEmailModalOpen(false);
    setEmailForm({ subject: '', content: '', senderEmail: '' });
    setEmailFiles([]);
    setEmailError(null);
  };

  const handleEmailInputChange = (event) => {
    const { name, value } = event.target;
    setEmailForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    const maxFiles = 10;
    const currentCount = emailFiles.length;
    const availableSlots = maxFiles - currentCount;

    if (availableSlots <= 0) {
      setEmailError('Maximum 10 files allowed');
      return;
    }

    const filesToProcess = selectedFiles.slice(0, availableSlots);

    const processedFiles = await Promise.all(
      filesToProcess.map(async (file) => {
        const base64Content = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result;
            const base64 = result.split(',')[1];
            resolve(base64);
          };
          reader.readAsDataURL(file);
        });

        return {
          name: file.name,
          mime_type: file.type || 'application/octet-stream',
          content: base64Content,
          size: file.size,
        };
      }),
    );

    setEmailFiles((prev) => [...prev, ...processedFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index) => {
    setEmailFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAiAssistant = async () => {
    if (!authHeaders || !emailForm.content.trim()) {
      setEmailError('Please enter some content to improve');
      return;
    }

    setAiAssistantLoading(true);
    setEmailError(null);

    try {
      const response = await axios.post(
        '/api/clients/writer-assistant',
        {
          text: emailForm.content,
          client_email: client?.email || undefined,
        },
        { headers: authHeaders },
      );

      if (response.data?.response) {
        setEmailForm((prev) => ({ ...prev, content: response.data.response }));
      }
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'AI assistant failed';
      setEmailError(message);
    } finally {
      setAiAssistantLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!authHeaders || !clientId) {
      return;
    }

    const trimmedSubject = emailForm.subject.trim();
    const trimmedContent = emailForm.content.trim();
    const trimmedSender = emailForm.senderEmail.trim();

    if (!trimmedSubject || !trimmedContent) {
      setEmailError('Subject and content are required');
      return;
    }

    setEmailSending(true);
    setEmailError(null);

    try {
      const payload = {
        subject: trimmedSubject,
        content: trimmedContent,
        sender_email: trimmedSender || undefined,
      };

      if (emailFiles.length > 0) {
        payload.files = emailFiles.map((file) => ({
          name: file.name,
          mime_type: file.mime_type,
          content: file.content,
        }));
      }

      await axios.post(`/api/clients/${clientId}/send-email`, payload, { headers: authHeaders });

      setEmailSuccess('Email sent successfully');
      fetchTickets(1);
      closeEmailModal(true);
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Failed to send email';
      setEmailError(message);
    } finally {
      setEmailSending(false);
    }
  };

  const openEditModal = () => {
    setEditError(null);
    setEditForm({
      name: client?.name || '',
      email: client?.email || '',
      phone_number: client?.phone_number || '',
      address_line: client?.address?.line1 || '',
      postal_code: client?.address?.postal_code || '',
      city: client?.address?.city || '',
      country: client?.address?.country || '',
    });
    setIsEditOpen(true);
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveClientEdit = async () => {
    if (!authHeaders || !clientId) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const response = await axios.patch(
        `/api/clients/${clientId}`,
        {
          name: editForm.name,
          email: editForm.email,
          phone_number: editForm.phone_number,
          address_line: editForm.address_line,
          postal_code: editForm.postal_code,
          city: editForm.city,
          country: editForm.country,
        },
        { headers: authHeaders },
      );
      setClient(response.data);
      setIsEditOpen(false);
    } catch (err) {
      setEditError(err.response?.data?.detail || err.message || 'Failed to update client');
    } finally {
      setEditSaving(false);
    }
  };

  const handleToggleVip = async () => {
    if (!authHeaders || !clientId || !client) {
      return;
    }

    setVipUpdating(true);
    setClientError(null);

    try {
      const newVipStatus = !client.is_vip;
      await axios.patch(
        `/api/clients/${clientId}/vip`,
        { is_vip: newVipStatus },
        { headers: authHeaders }
      );

      setClient((prev) => ({
        ...prev,
        is_vip: newVipStatus,
        updated_at: new Date().toISOString(),
      }));
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Failed to update VIP status';
      setClientError(message);
    } finally {
      setVipUpdating(false);
    }
  };

  const ticketState = useMemo(() => {
    if (ticketsLoading) {
      return (
        <div className="client-tickets-state">
          <div className="spinner" />
          <span>Loading tickets…</span>
        </div>
      );
    }

    if (ticketsError) {
      return (
        <div className="client-tickets-state client-tickets-state-error">
          <FaExclamationTriangle />
          <p>{ticketsError}</p>
          <button type="button" onClick={handleRetryTickets}>
            Retry
          </button>
        </div>
      );
    }

    if (!tickets.length) {
      return (
        <div className="client-tickets-state client-tickets-state-empty">
          <FaTicketAlt />
          <p>No tickets found for this client</p>
        </div>
      );
    }

    return (
      <div className="client-tickets-list">
        {tickets.map((ticket) => (
          <article
            key={ticket.id}
            className="client-ticket-card"
            onClick={() => handleTicketClick(ticket.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleTicketClick(ticket.id);
              }
            }}
          >
            <header>
              <div className="client-ticket-title">
                <FaTicketAlt />
                <h3>{ticket.subject || 'No subject'}</h3>
              </div>
              <span className={`client-ticket-status client-ticket-status-${ticket.status || 'unknown'}`}>
                {ticket.status || 'unknown'}
              </span>
            </header>

            {ticket.brand?.name && (
              <div className="client-ticket-brand">
                <FaStore />
                <span>{ticket.brand.name}</span>
              </div>
            )}

            <div className="client-ticket-meta">
              <div className="client-ticket-meta-item">
                <FaClock />
                <span>Created: {formatDateTime(ticket.created_at)}</span>
              </div>
              <div className="client-ticket-meta-item">
                <FaClock />
                <span>Updated: {formatDateTime(ticket.updated_at)}</span>
              </div>
            </div>

            {ticket.topic && (
              <div className="client-ticket-topic">
                <FaTag />
                <span>{ticket.topic}</span>
              </div>
            )}

            {Array.isArray(ticket.tags) && ticket.tags.length > 0 && (
              <div className="client-ticket-tags">
                {ticket.tags.map((tag) => (
                  <span key={tag} className="client-ticket-tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </article>
        ))}
      </div>
    );
  }, [tickets, ticketsLoading, ticketsError, ticketsHasMore, handleRetryTickets]);

  if (!authHeaders) {
    return null;
  }

  return (
    <div className="client-details-container">
      <header className="client-details-header">
        <button type="button" className="client-back-button" onClick={handleBack}>
          <FaArrowLeft />
          {t('clientDetails.backToB2C', 'Retour aux clients B2C')}
        </button>
        <div className="client-header-actions">
          <button
            type="button"
            className="client-edit-button"
            onClick={openEditModal}
            disabled={clientLoading || !client}
          >
            <FaPen />
            Edit
          </button>
          <button
            type="button"
            className={`client-vip-toggle-button ${client?.is_vip ? 'client-vip-toggle-active' : ''}`}
            onClick={handleToggleVip}
            disabled={vipUpdating || clientLoading || !client}
            title={client?.is_vip ? 'Remove VIP status' : 'Mark as VIP'}
          >
            <FaCrown />
            {vipUpdating ? 'Updating...' : client?.is_vip ? 'Remove VIP' : 'Mark as VIP'}
          </button>
          <button
            type="button"
            className="client-email-button"
            onClick={openEmailModal}
          >
            <FaPaperPlane />
            Send email
          </button>
          <button
            type="button"
            className="client-refresh-button"
            onClick={handleRefresh}
            disabled={ticketsRefreshing || ticketsLoading}
          >
            <FaSyncAlt />
            Refresh tickets
          </button>
        </div>
      </header>

      {emailSuccess && (
        <div className="client-email-alert client-email-alert-success">
          {emailSuccess}
        </div>
      )}

      <section className="client-summary">
        <div className="client-summary-card">
          <div className="client-summary-avatar">
            <FaUser />
          </div>
          {clientLoading ? (
            <div className="client-summary-loading">
              <div className="spinner" />
              <span>Loading client…</span>
            </div>
          ) : clientError ? (
            <div className="client-summary-error">
              <FaExclamationTriangle />
              <p>{clientError}</p>
            </div>
          ) : client ? (
            <div className="client-summary-content">
              <div className="client-summary-header">
                <h1>{client.name}</h1>
                {client.is_vip && (
                  <span className="client-vip-badge">
                    <FaCrown />
                    VIP Client
                  </span>
                )}
              </div>
              <div className="client-summary-meta">
                {client.email && (
                  <div className="client-summary-meta-item">
                    <FaEnvelope />
                    <span>{client.email}</span>
                  </div>
                )}
                {client.phone_number && (
                  <div className="client-summary-meta-item">
                    <FaPhoneAlt />
                    <span>{client.phone_number}</span>
                  </div>
                )}
                {formatAddress(client.address) && (
                  <div className="client-summary-meta-item">
                    <FaMapMarkerAlt />
                    <span>{formatAddress(client.address)}</span>
                    {client.address_status === 'needs_review' && (
                      <span className="client-address-review-badge" title="Address could not be verified automatically. Please review and update it.">
                        <FaExclamationTriangle /> To verify
                      </span>
                    )}
                  </div>
                )}
                {client.source && (
                  <div className="client-summary-meta-item">
                    <FaTag />
                    <span>Source: {client.source}</span>
                  </div>
                )}
              </div>
              <div className="client-summary-dates">
                <span>Created: {formatDateTime(client.created_at)}</span>
                <span>Updated: {formatDateTime(client.updated_at)}</span>
              </div>
            </div>
          ) : (
            <div className="client-summary-error">
              <FaExclamationTriangle />
              <p>Client not found</p>
            </div>
          )}
        </div>
      </section>

      <section className="client-tickets-section">
        <div className="client-tickets-header">
          <h2>Tickets</h2>
          <div className="client-tickets-pagination">
            <button
              type="button"
              onClick={handlePrevPage}
              disabled={ticketsPage === 1 || ticketsLoading}
            >
              Previous
            </button>
            <span>Page {ticketsPage}</span>
            <button
              type="button"
              onClick={handleNextPage}
              disabled={!ticketsHasMore || ticketsLoading}
            >
              Next
            </button>
          </div>
        </div>

        {ticketState}
      </section>

      {(voiceLoading || (voiceData && (voiceData.memory || (voiceData.conversations && voiceData.conversations.length > 0)))) && (
        <section className="client-voice-section">
          <div className="client-tickets-header">
            <h2><FaPhoneAlt /> {t('voiceTickets.clientSection.title', 'Vocal')}</h2>
          </div>

          {voiceLoading ? (
            <div className="client-tickets-state">
              <div className="spinner" />
              <span>{t('voiceTickets.clientSection.loading', 'Chargement des données vocales…')}</span>
            </div>
          ) : (
            <div className="client-voice-content">
              {voiceData.memory && (
                <div className="client-voice-memory">
                  <h3><FaBrain /> {t('voiceTickets.detail.callerProfile', 'Profil de l’appelant')}</h3>
                  {voiceData.memory.email && (
                    <div className="client-voice-fact">
                      <span>{t('voiceTickets.detail.email', 'E-mail')}</span>
                      <b>{voiceData.memory.email}</b>
                    </div>
                  )}
                  {voiceData.memory.postal_address && (
                    <div className="client-voice-fact">
                      <span>{t('voiceTickets.detail.address', 'Adresse')}</span>
                      <b>{voiceData.memory.postal_address}</b>
                    </div>
                  )}
                  <div className="client-voice-fact">
                    <span>{t('voiceTickets.detail.callCount', 'Appels')}</span>
                    <b>{t('voiceTickets.detail.callsTotal', '{{n}} au total', { n: voiceData.memory.call_count ?? 0 })}</b>
                  </div>
                  {voiceData.memory.summary && (
                    <>
                      <div className="client-voice-divider" />
                      <p className="client-voice-summary">{voiceData.memory.summary}</p>
                    </>
                  )}
                  {Array.isArray(voiceData.memory.preferences) && voiceData.memory.preferences.length > 0 && (
                    <div className="vt-chips">
                      {voiceData.memory.preferences.map((pref, index) => (
                        <span key={index} className="vt-chip neutral">{pref}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="client-voice-calls">
                <h3><FaComments /> {t('voiceTickets.clientSection.calls', 'Appels')}</h3>
                {(!voiceData.conversations || voiceData.conversations.length === 0) ? (
                  <p className="client-voice-empty">{t('voiceTickets.clientSection.noCalls', 'Aucune conversation vocale.')}</p>
                ) : (
                  voiceData.conversations.map((conv) => {
                    const convChips = outcomeChips(conv.tool_names, t);
                    return (
                      <article
                        key={conv.call_id}
                        className="client-voice-call-card"
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/voice-tickets/${encodeURIComponent(conv.call_id)}`)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            navigate(`/voice-tickets/${encodeURIComponent(conv.call_id)}`);
                          }
                        }}
                      >
                        <div className="client-voice-call-main">
                          <FaPhoneAlt />
                          <span>{formatCallDay(conv.started_at)}</span>
                          {convChips.length > 0 ? (
                            <span className={`vt-chip ${convChips[0].kind}`}>{convChips[0].label}</span>
                          ) : (
                            <span className="vt-chip info">{t('voiceTickets.facetInfo', 'Info seule')}</span>
                          )}
                        </div>
                        <div className="client-voice-call-meta">
                          <span><FaClock /> {formatVoiceDuration(conv.duration_seconds)}</span>
                          <span><FaComments /> {t('voiceTickets.turnsShort', '{{n}} éch.', { n: conv.turn_count ?? 0 })}</span>
                        </div>
                      </article>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </section>
      )}

      {isEditOpen && (
        <div className="client-email-modal">
          <div className="client-email-modal-backdrop" onClick={() => !editSaving && setIsEditOpen(false)} />
          <div className="client-email-modal-content">
            <header className="client-email-modal-header">
              <h3>Edit client</h3>
              <button type="button" onClick={() => setIsEditOpen(false)} className="client-email-modal-close" aria-label="Close" disabled={editSaving}>
                <FaTimes />
              </button>
            </header>
            <div className="client-email-modal-body">
              {editError && <div className="client-email-alert client-email-alert-error">{editError}</div>}
              <div className="client-email-field">
                <label htmlFor="client-edit-name">Name</label>
                <input id="client-edit-name" name="name" type="text" value={editForm.name} onChange={handleEditChange} placeholder="Full name" />
              </div>
              <div className="client-email-field">
                <label htmlFor="client-edit-email">Email</label>
                <input id="client-edit-email" name="email" type="email" value={editForm.email} onChange={handleEditChange} placeholder="name@example.com" />
              </div>
              <div className="client-email-field">
                <label htmlFor="client-edit-phone">Phone number</label>
                <input id="client-edit-phone" name="phone_number" type="tel" value={editForm.phone_number} onChange={handleEditChange} placeholder="+33612345678" />
              </div>
              <div className="client-email-field">
                <label htmlFor="client-edit-address-line">Address (street &amp; number)</label>
                <input id="client-edit-address-line" name="address_line" type="text" value={editForm.address_line} onChange={handleEditChange} placeholder="3 passage du docteur Calmette" />
              </div>
              <div className="client-edit-row">
                <div className="client-email-field">
                  <label htmlFor="client-edit-postal">Zip code</label>
                  <input id="client-edit-postal" name="postal_code" type="text" value={editForm.postal_code} onChange={handleEditChange} placeholder="06800" />
                </div>
                <div className="client-email-field">
                  <label htmlFor="client-edit-city">City</label>
                  <input id="client-edit-city" name="city" type="text" value={editForm.city} onChange={handleEditChange} placeholder="Cagnes-sur-Mer" />
                </div>
              </div>
              <div className="client-email-field">
                <label htmlFor="client-edit-country">Country</label>
                <input id="client-edit-country" name="country" type="text" value={editForm.country} onChange={handleEditChange} placeholder="France" />
              </div>
            </div>
            <footer className="client-email-modal-footer">
              <button type="button" className="client-email-cancel" onClick={() => setIsEditOpen(false)} disabled={editSaving}>
                Cancel
              </button>
              <button type="button" className="client-email-submit" onClick={handleSaveClientEdit} disabled={editSaving}>
                {editSaving ? 'Saving…' : 'Save'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {isEmailModalOpen && (
        <div className="client-email-modal">
          <div className="client-email-modal-backdrop" onClick={closeEmailModal} />
          <div className="client-email-modal-content">
            <header className="client-email-modal-header">
              <h3>Send email to {client?.name || 'client'}</h3>
              <button type="button" onClick={closeEmailModal} className="client-email-modal-close" aria-label="Close">
                <FaTimes />
              </button>
            </header>
            <div className="client-email-modal-body">
              {emailError && <div className="client-email-alert client-email-alert-error">{emailError}</div>}
              <div className="client-email-field">
                <label htmlFor="client-email-subject">Subject</label>
                <input
                  id="client-email-subject"
                  name="subject"
                  type="text"
                  value={emailForm.subject}
                  onChange={handleEmailInputChange}
                  placeholder="Enter subject"
                />
              </div>
              <div className="client-email-field">
                <div className="client-email-content-header">
                  <label htmlFor="client-email-content">Content</label>
                  <button
                    type="button"
                    className="client-email-ai-button"
                    onClick={handleAiAssistant}
                    disabled={aiAssistantLoading || !emailForm.content.trim()}
                    title="Improve with AI"
                  >
                    <FaMagic />
                    {aiAssistantLoading ? 'Improving...' : 'AI Assist'}
                  </button>
                </div>
                <textarea
                  id="client-email-content"
                  name="content"
                  rows={8}
                  value={emailForm.content}
                  onChange={handleEmailInputChange}
                  placeholder="Write your message"
                  disabled={aiAssistantLoading}
                />
              </div>
              <div className="client-email-field">
                <label htmlFor="client-email-sender">Sender email (optional)</label>
                <input
                  id="client-email-sender"
                  name="senderEmail"
                  type="email"
                  value={emailForm.senderEmail}
                  onChange={handleEmailInputChange}
                  placeholder="Override default Outlook sender"
                />
              </div>
              <div className="client-email-field">
                <label>Attachments</label>
                <div className="client-email-file-upload">
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="client-email-files"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="client-email-file-button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={emailFiles.length >= 10}
                  >
                    <FaPaperclip />
                    Add files
                  </button>
                  <span className="client-email-file-hint">
                    {emailFiles.length}/10 files
                  </span>
                </div>
                {emailFiles.length > 0 && (
                  <div className="client-email-file-list">
                    {emailFiles.map((file, index) => (
                      <div key={`${file.name}-${index}`} className="client-email-file-item">
                        <span className="client-email-file-name">{file.name}</span>
                        <span className="client-email-file-size">
                          {(file.size / 1024).toFixed(1)} KB
                        </span>
                        <button
                          type="button"
                          className="client-email-file-remove"
                          onClick={() => handleRemoveFile(index)}
                          aria-label={`Remove ${file.name}`}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <footer className="client-email-modal-footer">
              <button type="button" className="client-email-cancel" onClick={closeEmailModal} disabled={emailSending}>
                Cancel
              </button>
              <button type="button" className="client-email-submit" onClick={handleSendEmail} disabled={emailSending}>
                {emailSending ? 'Sending…' : 'Send email'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDetails;
